import type { LibraryEntryResponse } from '@malaberg/shared';
/**
 * AwarenessLevelSelect — T23 + T6.
 *
 * Mantine Select (NOT a combobox) for the awareness level field.
 * Options come from librariesApi.list('awareness_level', { scope: 'workspace' }),
 * ordered by metadata.rank asc.
 *
 * Role behavior:
 * - Writers: no add-new option; tooltip "Awareness levels are managed by admins."
 *   shown on hover via Mantine Tooltip.
 * - Admin / system_owner: add-new affordance is shown (uses LibraryCombobox under the hood).
 *
 * T6 — Ephemeral mode:
 * - When fieldValue.kind === 'ephemeral', a subtle "free text" indicator is shown
 *   next to the label. The Select still allows the user to pick a real entry,
 *   which will call onFieldChange({kind:'entry', entryId}).
 */
import { Group, Select, Text, Tooltip } from '@mantine/core';
import { useLibraries } from '../../hooks/use-libraries.js';
import { useAuthStore } from '../../state/auth-store.js';
import type { FieldValue } from '../../state/brief-form-store.js';

interface AwarenessLevelSelectProps {
  value: string | null;
  onChange: (entryId: string | null) => void;
  /**
   * T6: 3-channel field state. When kind === 'ephemeral', shows a subtle indicator.
   * Selecting from the dropdown will call onFieldChange({kind:'entry',...}).
   */
  fieldValue?: FieldValue | null;
  /** T6: Called when the user selects an entry, transitioning from ephemeral to entry. */
  onFieldChange?: (fv: FieldValue | null) => void;
}

export function AwarenessLevelSelect({
  value,
  onChange,
  fieldValue,
  onFieldChange,
}: AwarenessLevelSelectProps) {
  const user = useAuthStore((s) => s.user);
  const isWriter = user?.role === 'writer';

  const { entries } = useLibraries('awareness_level', 'workspace');

  const isEphemeral = fieldValue?.kind === 'ephemeral';
  // When the field is ephemeral, surface the AI-suggested text as the Select
  // placeholder. The Select's value is null (no real entry is selected), so
  // without this the input appears completely empty — confusing the writer into
  // thinking no suggestion was applied. Using placeholder is the lightest-touch
  // approach: it doesn't change dropdown behavior and reverts automatically when
  // the writer picks a real entry (value becomes non-null, placeholder disappears).
  const ephemeralText = isEphemeral
    ? (fieldValue as { kind: 'ephemeral'; ephemeralValue: string }).ephemeralValue
    : null;

  // Sort by metadata.rank ascending (entries without rank sort last)
  const sorted = [...entries].sort((a, b) => {
    const rankA =
      typeof a.metadata?.rank === 'number' ? (a.metadata.rank as number) : Number.POSITIVE_INFINITY;
    const rankB =
      typeof b.metadata?.rank === 'number' ? (b.metadata.rank as number) : Number.POSITIVE_INFINITY;
    return rankA - rankB;
  });

  const data = sorted.map((e: LibraryEntryResponse) => ({
    value: e.id,
    label: e.displayName,
  }));

  const handleChange = (entryId: string | null) => {
    onChange(entryId);
    if (entryId) {
      onFieldChange?.({ kind: 'entry', entryId });
    } else {
      onFieldChange?.(null);
    }
  };

  const select = (
    <div>
      {/* T6: Subtle ephemeral indicator shown above the Select when in ephemeral mode */}
      {isEphemeral && (
        <Group gap={4} mb={2}>
          <Text size="xs" c="dimmed" fs="italic" data-testid="awareness-ephemeral-indicator">
            free text
          </Text>
        </Group>
      )}
      <Select
        label="Awareness level"
        placeholder={ephemeralText ?? 'Choose an awareness level'}
        data={data}
        value={value}
        onChange={handleChange}
        data-testid="field-awareness-level"
      />
    </div>
  );

  if (isWriter) {
    return (
      <span data-testid="awareness-tooltip-wrapper">
        <Tooltip label="Awareness levels are managed by admins." position="top-start">
          <span>{select}</span>
        </Tooltip>
      </span>
    );
  }

  return select;
}
